import BreadCrumb from "Common/BreadCrumb";
import { ArrowLeft, FileEdit, Package } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import type { AppDispatch } from "../../store";
import { RootState } from "../../slices";
import { Customer } from "../../slices/customers/reducer";
import { getOrders } from "../../slices/orders/thunk";
import { PaymentForm } from "../../slices/paymentForms/reducer";
import { PaymentMethod } from "../../slices/paymentMethods/reducer";
import { Product } from "../../slices/products/reducer";
import { StatusBadge } from "./components/StatusBadge";

const getCustomerById = (customers: Customer[], id: string | number) => {
  const customer = customers.find((c) => c.id === id);
  return customer
    ? {
        label: customer.name,
        document: `ID: ${id}`,
      }
    : {
        label: `Cliente ID: ${id}`,
        document: "Não encontrado",
      };
};

const getPaymentMethodById = (paymentMethods: PaymentMethod[], id: string | number) => {
  const method = paymentMethods.find((m) => m.id === id);
  return method
    ? {
        label: method.description,
      }
    : {
        label: `Método ID: ${id}`,
      };
};

const getPaymentFormById = (paymentForms: PaymentForm[], id: string | number) => {
  const form = paymentForms.find((f) => f.id === id);
  return form
    ? {
        label: form.description,
      }
    : {
        label: `Forma ID: ${id}`,
      };
};

const getProductById = (products: Product[], id: string | number) => {
  const product = products.find((p) => p.id === id);
  return product
    ? {
        description: product.description,
        unitPrice: product.unitPrice,
        promotionPrice: product.promotionPrice,
      }
    : null;
};

const OrderView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const ordersState = useSelector((state: RootState) => state.Orders);
  const productsState = useSelector((state: RootState) => state.Products);
  const customersState = useSelector((state: RootState) => state.Customers);
  const paymentMethodsState = useSelector((state: RootState) => state.PaymentMethods);
  const paymentFormsState = useSelector((state: RootState) => state.PaymentForms);

  const orders = ordersState.orders || [];
  const products = productsState.products || [];
  const customers = customersState.customers || [];
  const paymentMethods = paymentMethodsState.paymentMethods || [];
  const paymentForms = paymentFormsState.paymentForms || [];

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const preservedState = location.state as { activeTab?: string; searchTerm?: string } | null;

  const order = orders.find((o) => o.id === Number.parseInt(id || "0", 10)) || null;

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError("ID do pedido não fornecido");
        setIsLoading(false);
        return;
      }

      try {
        if (orders.length === 0 || !order) {
          await dispatch(getOrders());
        }
        setIsLoading(false);
      } catch (err) {
        console.error("Erro ao carregar pedido:", err);
        setError("Erro ao carregar dados do pedido");
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, dispatch, orders.length, order]);

  const handleGoBack = () => {
    const searchParams = new URLSearchParams();

    if (preservedState?.activeTab) {
      searchParams.set("tab", preservedState.activeTab);
    }

    if (preservedState?.searchTerm) {
      searchParams.set("search", preservedState.searchTerm);
    }

    const queryString = searchParams.toString();
    const finalUrl = queryString ? `/sale-orders?${queryString}` : "/sale-orders";
    navigate(finalUrl, {
      state: preservedState,
    });
  };

  if (isLoading) {
    return (
      <>
        <BreadCrumb title="Carregando..." pageTitle="Pedidos de Venda" />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-2">
            <svg
              className="w-6 h-6 text-custom-500 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-slate-500">Carregando dados do pedido...</span>
          </div>
        </div>
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <BreadCrumb title="Erro" pageTitle="Pedidos de Venda" />
        <div className="card">
          <div className="py-12 text-center card-body">
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-slate-700 dark:text-slate-200">
              Pedido não encontrado
            </h3>
            <p className="mb-6 text-slate-500 dark:text-slate-400">
              {error || "Não foi possível carregar os dados do pedido."}
            </p>
            <button
              onClick={handleGoBack}
              className="inline-flex items-center px-4 py-2 text-white border rounded-md bg-custom-500 border-custom-500 hover:bg-custom-600 focus:outline-none focus:ring-2 focus:ring-custom-500 focus:ring-offset-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar aos Pedidos
            </button>
          </div>
        </div>
      </>
    );
  }

  const formatCurrency = (value: string | number) => {
    const numValue = typeof value === "string" ? Number.parseFloat(value) : value;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numValue);
  };

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(dateObj);
  };

  return (
    <>
      <BreadCrumb title={`Pedido #${order.id}`} pageTitle="Pedidos de Venda" />

      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleGoBack}
          className="inline-flex items-center px-4 py-2 border rounded-md text-slate-600 bg-slate-100 border-slate-200 hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar aos Pedidos
        </button>

        <div className="flex items-center space-x-3">
          <span className="text-sm text-slate-500 dark:text-slate-400">Status:</span>
          <StatusBadge status={order.status as 0 | 1 | 2} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 max-h-[calc(100vh-200px)] overflow-y-auto">
        <div className="card">
          <div className="card-body">
            <h2 className="mb-6 text-xl font-semibold text-slate-800 dark:text-slate-200">
              Informações do Pedido
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  ID do Pedido
                </span>
                <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                  #{order.id}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Cliente/Fornecedor
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {getCustomerById(customers, order.customerId)?.label || order.customerName}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {getCustomerById(customers, order.customerId)?.document ||
                    `ID: ${order.customerId}`}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Data de Emissão
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {formatDate(order.dateEmission)}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Data do Pedido
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {new Date(order.dateEmission).toLocaleDateString("pt-BR")}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Data de Entrega Prevista
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {new Date(
                    new Date(order.dateEmission).getTime() + 5 * 24 * 60 * 60 * 1000
                  ).toLocaleDateString("pt-BR")}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Status da Entrega
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {(() => {
                    if (order.status === 1) return "Pendente de Entrega";
                    if (order.status === 2) return "Entregue";
                    return "Não informado";
                  })()}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Documento do Proprietário
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {order.documentOwner || "Não informado"}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {(() => {
                    if (!order.documentOwner) return "";
                    if (order.documentOwner.length === 11) return "CPF";
                    if (order.documentOwner.length === 14) return "CNPJ";
                    return "";
                  })()}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Método de Pagamento
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {getPaymentMethodById(paymentMethods, order.paymentMethodId)?.label ||
                    order.paymentMethodName}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Forma de Pagamento
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {order.paymentFormId
                    ? getPaymentFormById(paymentForms, order.paymentFormId)?.label ||
                      order.paymentFormName
                    : "Não informado"}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Valor dos Produtos
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {order.amountProducts ? formatCurrency(order.amountProducts) : "Não informado"}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Valor da Promoção
                </span>
                <p className="text-base text-slate-800 dark:text-slate-200">
                  {order.amountPromotion ? formatCurrency(order.amountPromotion) : "R$ 0,00"}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Valor Total
                </span>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(order.amountProducts - order.amountPromotion)}
                </p>
              </div>

              <div>
                <span className="inline-block mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Status do Pedido
                </span>
                <div className="mt-1">
                  <StatusBadge status={order.status as 0 | 1 | 2} />
                </div>
              </div>
            </div>

            {order.observation && (
              <div className="pt-6 mt-6 border-t border-slate-200 dark:border-slate-600">
                <span className="inline-block mb-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                  Observações do Pedido
                </span>
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <p className="text-base leading-relaxed text-slate-700 dark:text-slate-300">
                    {order.observation}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {order.items && order.items.length > 0 && (
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Itens do Pedido
                </h3>
                <span className="px-3 py-1 text-sm font-medium rounded-full bg-custom-100 text-custom-600 dark:bg-custom-500/20 dark:text-custom-400">
                  {order.items.length} {order.items.length === 1 ? "item" : "itens"}
                </span>
              </div>

              <div className="space-y-4">
                {order.items.map((item, index) => {
                  const product = getProductById(products, item.idProduct);
                  const subtotal =
                    Number(item.quantity) * Number(item.promotionPrice || item.unitPrice);
                  const hasPromotion =
                    item.promotionPrice && Number(item.promotionPrice) !== Number(item.unitPrice);

                  return (
                    <div
                      key={item.id || index}
                      className="p-4 border rounded-lg border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <Package className="w-5 h-5 mr-2 text-slate-500 dark:text-slate-400" />
                            <h4 className="font-medium text-slate-800 dark:text-slate-200">
                              {product?.description || `Produto ID: ${item.idProduct}`}
                            </h4>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mt-3 text-sm md:grid-cols-4">
                            <div>
                              <span className="block font-medium text-slate-600 dark:text-slate-400">
                                Quantidade
                              </span>
                              <span className="text-slate-800 dark:text-slate-200">
                                {item.quantity} un
                              </span>
                            </div>

                            <div>
                              <span className="block font-medium text-slate-600 dark:text-slate-400">
                                Preço Unitário
                              </span>
                              <span className="text-slate-800 dark:text-slate-200">
                                {formatCurrency(item.unitPrice)}
                              </span>
                            </div>

                            {hasPromotion && (
                              <div>
                                <span className="block font-medium text-slate-600 dark:text-slate-400">
                                  Preço Promocional
                                </span>
                                <span className="font-medium text-green-600 dark:text-green-400">
                                  {formatCurrency(item.promotionPrice)}
                                </span>
                              </div>
                            )}

                            <div>
                              <span className="block font-medium text-slate-600 dark:text-slate-400">
                                Subtotal
                              </span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">
                                {formatCurrency(subtotal)}
                              </span>
                            </div>
                          </div>

                          {item.observation && (
                            <div className="mt-3">
                              <span className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                Observação do Item
                              </span>
                              <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                                {item.observation}
                              </p>
                            </div>
                          )}

                          {hasPromotion && (
                            <div className="flex items-center mt-3 text-sm text-green-600 dark:text-green-400">
                              <span className="flex items-center">
                                <svg
                                  className="w-4 h-4 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Economia:{" "}
                                {formatCurrency(
                                  (Number(item.unitPrice) - Number(item.promotionPrice)) *
                                    Number(item.quantity)
                                )}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex-shrink-0 ml-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-full bg-custom-100 text-custom-600 dark:bg-custom-500/20 dark:text-custom-400">
                            {index + 1}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-4 mt-6 border-t border-slate-200 dark:border-slate-600">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="text-center">
                    <span className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                      Total de Itens
                    </span>
                    <span className="text-lg font-bold text-slate-800 dark:text-slate-200">
                      {order.items.reduce((sum, item) => sum + Number(item.quantity), 0)} unidades
                    </span>
                  </div>

                  <div className="text-center">
                    <span className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                      Valor Total dos Produtos
                    </span>
                    <span className="text-lg font-bold text-slate-800 dark:text-slate-200">
                      {formatCurrency(order.amountProducts)}
                    </span>
                  </div>

                  <div className="text-center">
                    <span className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                      Valor Final
                    </span>
                    <span className="text-xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(order.amountProducts - order.amountPromotion)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <h3 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-200">
              Ações Disponíveis
            </h3>

            <div className="flex flex-col gap-3 sm:flex-row">
              {order.status !== 2 && (
                <button
                  onClick={() => navigate(`/sale-orders/edit/${order.id}`)}
                  className="inline-flex items-center justify-center px-4 py-2 text-white border rounded-md bg-custom-500 border-custom-500 hover:bg-custom-600 focus:outline-none focus:ring-2 focus:ring-custom-500 focus:ring-offset-2"
                >
                  <FileEdit className="w-4 h-4 mr-2" />
                  Editar Pedido
                </button>
              )}

              <button
                onClick={handleGoBack}
                className="inline-flex items-center justify-center px-4 py-2 border rounded-md text-slate-600 bg-slate-100 border-slate-200 hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar aos Pedidos
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderView;
